import type { ReactNode } from "react";

interface EmptyStateProps {
  title: string;
  body?: string;
  icon?: "deals" | "commissions" | "invoices" | "webhooks" | "search" | "payouts" | "generic";
  actionHref?: string;
  actionLabel?: string;
  tone?: "default" | "subtle";
  children?: ReactNode;
}

const icons: Record<NonNullable<EmptyStateProps["icon"]>, string> = {
  deals:
    '<path d="M4 8h24M4 16h24M4 24h24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.35"/><rect x="7" y="6" width="12" height="4" rx="1" fill="currentColor" opacity="0.2"/><rect x="7" y="14" width="16" height="4" rx="1" fill="currentColor" opacity="0.3"/><rect x="7" y="22" width="9" height="4" rx="1" fill="currentColor" opacity="0.2"/>',
  commissions:
    '<circle cx="16" cy="16" r="10" stroke="currentColor" stroke-width="1.5" opacity="0.4"/><path d="M16 10v6l4 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="16" cy="16" r="1.5" fill="currentColor"/>',
  invoices:
    '<rect x="7" y="5" width="18" height="22" rx="2" stroke="currentColor" stroke-width="1.5" opacity="0.4"/><path d="M11 11h10M11 15h10M11 19h7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.7"/>',
  webhooks:
    '<circle cx="9" cy="10" r="3" stroke="currentColor" stroke-width="1.5"/><circle cx="22" cy="10" r="3" stroke="currentColor" stroke-width="1.5"/><circle cx="16" cy="23" r="3" stroke="currentColor" stroke-width="1.5"/><path d="M11 12l4 8M21 12l-4 8M11 10h11" stroke="currentColor" stroke-width="1.5" opacity="0.45"/>',
  search:
    '<circle cx="14" cy="14" r="7" stroke="currentColor" stroke-width="1.5"/><path d="M20 20l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  payouts:
    '<rect x="4" y="10" width="24" height="16" rx="2" stroke="currentColor" stroke-width="1.5" opacity="0.4"/><circle cx="16" cy="18" r="3.5" stroke="currentColor" stroke-width="1.5"/><path d="M8 6l16 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.5"/>',
  generic:
    '<rect x="6" y="6" width="20" height="20" rx="4" stroke="currentColor" stroke-width="1.5" opacity="0.4"/><path d="M12 16h8M16 12v8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.5"/>',
};

export function EmptyState({
  title,
  body,
  icon = "generic",
  actionHref,
  actionLabel,
  tone = "default",
  children,
}: EmptyStateProps) {
  return (
    <div className={["empty-state", tone === "subtle" && "empty-state--subtle"].filter(Boolean).join(" ")}>
      <div className="empty-icon" aria-hidden>
        <svg viewBox="0 0 32 32" fill="none" dangerouslySetInnerHTML={{ __html: icons[icon] }} />
      </div>
      <p className="empty-title">{title}</p>
      {body && <p className="empty-body">{body}</p>}
      {children}
      {actionHref && actionLabel && (
        <a className="empty-action" href={actionHref}>
          {actionLabel}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path
              d="M3 6h6m0 0L6 3m3 3L6 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      )}
    </div>
  );
}
